import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuditLog } from '../../hooks/useAuditLog';
import { useActiveCompanyId } from '../../stores/useCompanyStore';

export const Route = createFileRoute('/_auth/audit-log/')({
  component: AuditLogPage,
});

const ENTITY_TYPES = [
  'journal_entries', 'accounts', 'reconciliations', 'checks', 'deposits', 'invoices', 'bills',
  'recurring_entries', 'fixed_assets', 'loans', 'intercompany_transactions', 'budgets',
  'year_end_closings', 'companies',
];
const ACTIONS = ['create', 'update', 'delete', 'void', 'complete', 'lock', 'unlock'];

function AuditLogPage() {
  const companyId = useActiveCompanyId();
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: entries = [], isLoading, error } = useAuditLog(companyId, {
    entityType: entityType || undefined,
    action: action || undefined,
    from: from || undefined,
    to: to || undefined,
  });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Audit Log</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const toggle = (id: string) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Audit Log</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Every create, change, void, and reconciliation, newest first. Read-only.
      </p>

      <div className="card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>
          Type{' '}
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            <option value="">all</option>
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>
          Action{' '}
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">all</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 13 }}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        {(entityType || action || from || to) && (
          <button className="btn-secondary" onClick={() => { setEntityType(''); setAction(''); setFrom(''); setTo(''); }}>Clear</button>
        )}
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load the audit log.</p>}

      {!isLoading && entries.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ margin: 0 }}>No audit entries match these filters.</p>
        </div>
      )}

      {entries.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Type</th>
              <th>Action</th>
              <th>Entity</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <Row key={e.id} entry={e} open={expanded.has(e.id)} onToggle={() => toggle(e.id)} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function actionTag(action: string) {
  const cls = action === 'create' ? 'ok' : action === 'void' || action === 'delete' ? 'bad' : 'warn';
  return <span className={`tag ${cls}`}>{action}</span>;
}

function Row({ entry, open, onToggle }: { entry: import('@accounting/shared').AuditLogEntry; open: boolean; onToggle: () => void }) {
  const hasDetail = entry.before != null || entry.after != null;
  return (
    <>
      <tr style={{ cursor: hasDetail ? 'pointer' : 'default' }} onClick={() => hasDetail && onToggle()}>
        <td style={{ whiteSpace: 'nowrap' }}>{new Date(entry.createdAt).toLocaleString()}</td>
        <td>{entry.entityType}</td>
        <td>{actionTag(entry.action)}</td>
        <td className="muted" style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {hasDetail ? (open ? '▾ ' : '▸ ') : ''}{entry.entityId.slice(0, 8)}
        </td>
        <td>{entry.actorOid ? <span className="muted" style={{ fontSize: 12 }}>by {entry.actorOid.slice(0, 8)}</span> : ''}</td>
      </tr>
      {open && hasDetail && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--bg-surface-hover)' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
              {entry.before != null && (
                <div style={{ flex: 1, minWidth: 240 }}>
                  <strong>Before</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{JSON.stringify(entry.before, null, 2)}</pre>
                </div>
              )}
              {entry.after != null && (
                <div style={{ flex: 1, minWidth: 240 }}>
                  <strong>After</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{JSON.stringify(entry.after, null, 2)}</pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
